import { useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'

import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { blue, green, lightBlue, lightGreen, orange, red, yellow } from '@material-ui/core/colors'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import type { Theme } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import classNames from 'classnames'

import FormField from '~/common/components/form-field'
import type { ProductFormSchema } from '~/products/hooks/useProductForm'
import { formatDate, getPath } from '~/utils'
import { getChannelPriceRatio } from '~/validators/helpers'
import {
    createPriceValidator,
    maxPriceInRange,
    minPriceRatio,
    required,
    shouldValidatePrice,
} from '~/validators/price'
import { ValidationFunction } from '~/validators/types'

const DEFAULT_FIELD_WIDTH = 125
const DISCOUNT_COLORS = [
    orange[400],
    green[400],
    blue[400],
    red[400],
    yellow[400],
    lightBlue[400],
    lightGreen[400],
    orange[600],
    green[600],
    blue[600],
    red[600],
    yellow[600],
    lightBlue[600],
    lightGreen[600],
]

const LightTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}))(Tooltip)

function PriceField(props: {
    activeChannelStockUnitDiscountIndex: number
    discountIndex: number
    fieldWidth: number
    channelProductIndex: number
    stockUnitIndex: number
}) {
    const classes = usePricingStyles({ fieldWidth: props.fieldWidth })

    const discount: ProductFormSchema['product_discounts'][number]['discount'] = useWatch({
        name: `product_discounts.${props.discountIndex}.discount`,
    })

    const channelId: number = useWatch({
        name: `channel_products.${props.channelProductIndex}.shop.channel.id`,
    })

    const price: string = useWatch({
        name: `channel_products.${props.channelProductIndex}.channel_stock_units.${props.stockUnitIndex}.price`,
        defaultValue: '0',
    })
    const originalPrice = parseFloat(price)

    const formState = useFormState({
        name: `active_channel_stock_unit_discounts.${props.activeChannelStockUnitDiscountIndex}`,
    })

    const hasErrors = !!formState.errors.active_channel_stock_unit_discounts

    return (
        <div style={{ display: 'flex' }}>
            <FormField
                fullWidth
                type="number"
                transform={{
                    input: (value: string | number) => value.toString(),
                    output: (e) => parseFloat(e.target.value),
                }}
                helperText={hasErrors ? <>&nbsp;</> : undefined}
                name={`active_channel_stock_unit_discounts.${props.activeChannelStockUnitDiscountIndex}.price`}
                placeholder={gettext('Discount Price')}
                InputProps={{ className: classNames(classes.priceField, classes.csudField) }}
                inputProps={{
                    min: originalPrice ? originalPrice * getChannelPriceRatio(channelId) : 0,
                    max: originalPrice,
                }}
                rules={{
                    validate: (value) => {
                        if (typeof value !== 'number') {
                            return gettext('Price must be a number')
                        }

                        const validators: ValidationFunction[] = [required()]
                        if (originalPrice && shouldValidatePrice(channelId)) {
                            validators.push(maxPriceInRange([originalPrice]))
                            validators.push(
                                minPriceRatio([originalPrice], {
                                    ratio: getChannelPriceRatio(channelId),
                                })
                            )
                        }

                        const validator = createPriceValidator(validators)
                        const result = validator.validate(value)

                        return result.success || result.error
                    },
                }}
            />
            <LightTooltip
                interactive
                title={
                    <div className={classes.discountInfo}>
                        <div>
                            <a
                                href={getPath('discount', {
                                    discount_id: discount.id.toString(),
                                })}
                                target="_blank"
                                rel="noreferrer"
                                className={classes.discountLink}
                            >
                                {discount.name}
                            </a>
                        </div>
                        {discount.start_date && (
                            <>
                                <div className={classes.discountDateLabel}>Start: </div>
                                <div>
                                    {formatDate(discount.start_date, {
                                        format: 'dddd, D MMM YYYY, HH:mm',
                                    })}
                                </div>
                            </>
                        )}
                        {discount.end_date && (
                            <>
                                <div className={classes.discountDateLabel}>End: </div>
                                <div>
                                    {formatDate(discount.end_date, {
                                        format: 'dddd, D MMM YYYY, HH:mm',
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                }
                placement="top-start"
                leaveDelay={1000}
            >
                <div
                    className={classes.discountIndicator}
                    style={{
                        background: DISCOUNT_COLORS[props.discountIndex % DISCOUNT_COLORS.length],
                    }}
                />
            </LightTooltip>
        </div>
    )
}

function ProductPricingSkuColumn(props: { suIndex: number; fieldWidth: number }) {
    const { suIndex, fieldWidth } = props
    const sku = useWatch({ name: `stock_units.${suIndex}.sku`, exact: true })
    const classes = usePricingStyles({ fieldWidth })

    return (
        <Typography className={classNames(classes.skuColumn, !sku && classes.skuPlaceholder)}>
            {sku || `${gettext('Stock unit')} ${suIndex + 1}`}
        </Typography>
    )
}

export const usePricingStyles = makeStyles<Theme, { fieldWidth: number }>((theme) => ({
    table: {
        display: 'block',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        borderSpacing: '10px 0',
        paddingBottom: '1rem',
    },
    priceFieldColumn: {
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: (props) => props.fieldWidth,
        },
    },
    priceField: {
        height: 35,
    },
    priceRow: {
        verticalAlign: 'text-top',
    },
    priceColumn: {
        maxWidth: (props) => props.fieldWidth,
        textAlign: 'center',
    },
    firstColumn: {
        width: (props) => props.fieldWidth,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    directionIcon: {
        color: theme.palette.grey[500],
    },
    pricingColumnHeader: {
        display: 'flex',
        alignItems: 'center',
    },
    pricingChannelIconURL: {
        width: 20,
        marginRight: 5,
    },
    pricingColumnShopName: {
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    skuPlaceholder: {
        color: 'grey',
    },
    skuColumn: {
        maxWidth: '13ch',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    discountIndicator: {
        width: 7,
        height: 35,
        background: '#ccc',
        margin: '8px 0 0 0',
        borderRadius: '0 4px 4px 0',
    },
    csudField: {
        borderRadius: '4px 0 0 4px',
        '& > fieldset': {
            borderRight: 'none',
        },
    },
    discountInfo: {
        fontWeight: 'normal',
        padding: 10,
    },
    discountDateLabel: {
        color: 'rgba(0, 0, 0, 0.6)',
        marginTop: '8px',
        fontSize: '9px',
    },
    discountLink: {
        fontSize: 14,
        color: '#000000',
        textDecoration: 'none',
        '&:hover': {
            color: orange[700],
        },
    },
}))

type Props = {
    channelProducts: ProductFormSchema['channel_products']
    stockUnits: ProductFormSchema['stock_units']
    activeDiscountIndexLookup: ReadonlyMap<string, number[]>
    discountIndexByCSUId: Record<string, number>
    fieldWidth?: number
    onSetPriceForChannelProduct: (
        cp: ProductFormSchema['channel_products'][number],
        value: number
    ) => void
    onSetPriceForStockUnits: (stockUnitId: number, value: number) => void
}

export default function PricingTable({
    channelProducts,
    stockUnits,
    fieldWidth = DEFAULT_FIELD_WIDTH,
    discountIndexByCSUId,
    activeDiscountIndexLookup,
    onSetPriceForChannelProduct,
    onSetPriceForStockUnits,
}: Props) {
    const classes = usePricingStyles({ fieldWidth })
    const hasMultipleChannels = channelProducts.length > 1

    const channelStockUnitLookup = useMemo(() => {
        const entries = channelProducts.flatMap((cp) => {
            return cp.channel_stock_units.map((csu) => {
                const key = `${cp.id}-${csu.stock_unit?.id}`
                return [key, csu] as const
            })
        })

        return new Map(entries)
    }, [channelProducts])

    return (
        <table className={classes.table}>
            <tbody>
                <tr>
                    <th scope="col" colSpan={hasMultipleChannels ? 2 : 1}></th>

                    {hasMultipleChannels &&
                        channelProducts.map((cp, cpIndex) => (
                            <th
                                key={cp.id || cpIndex}
                                scope="col"
                                className={classes.priceFieldColumn}
                            >
                                <div>
                                    <TextField
                                        type="number"
                                        variant="outlined"
                                        margin="dense"
                                        placeholder={gettext('Set all')}
                                        InputProps={{ className: classes.priceField }}
                                        disabled={cp.is_promotion_period}
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => {
                                            onSetPriceForChannelProduct(
                                                cp,
                                                parseFloat(e.target.value)
                                            )
                                        }}
                                    />
                                    <KeyboardArrowDownIcon className={classes.directionIcon} />
                                </div>
                            </th>
                        ))}
                </tr>

                <tr>
                    <td colSpan={hasMultipleChannels ? 2 : 1} />
                    {channelProducts.map((cp, cpIndex) => (
                        <td key={cp.id || cpIndex} className={classes.priceColumn}>
                            <div className={classes.pricingColumnHeader}>
                                <img
                                    alt={cp.shop.channel.name}
                                    className={classes.pricingChannelIconURL}
                                    src={cp.shop.channel.channel_square_icon_url}
                                />
                                <Typography className={classes.pricingColumnShopName}>
                                    {cp.shop.shop_name}
                                </Typography>
                            </div>
                        </td>
                    ))}
                </tr>

                {stockUnits.map((su, suIndex) => (
                    <tr key={su.id} className={classes.priceRow}>
                        {channelProducts.length > 1 && (
                            <td>
                                <div className={classes.firstColumn}>
                                    <TextField
                                        type="number"
                                        variant="outlined"
                                        margin="dense"
                                        placeholder={gettext('Set all')}
                                        InputProps={{ className: classes.priceField }}
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => {
                                            if (typeof su.id !== 'number') {
                                                return
                                            }

                                            onSetPriceForStockUnits(
                                                su.id,
                                                parseFloat(e.target.value)
                                            )
                                        }}
                                    />
                                    <KeyboardArrowRightIcon className={classes.directionIcon} />
                                </div>
                            </td>
                        )}

                        <td>
                            <ProductPricingSkuColumn suIndex={suIndex} fieldWidth={fieldWidth} />
                        </td>

                        {channelProducts.map((cp, cpIndex) => {
                            const csu = channelStockUnitLookup.get(`${cp.id}-${su.id}`)
                            if (!csu) {
                                return null
                            }

                            // Multiple active CSUDs may exist in cases where skus are present
                            // in multiple product listings for the shop
                            const activeChannelStockUnitDiscountIndices =
                                activeDiscountIndexLookup.get(`${cp.shop.id}_${su.id}`) ?? []

                            if (!activeChannelStockUnitDiscountIndices.length) {
                                return null
                            }

                            return (
                                <td key={cp.id} className={classes.priceColumn}>
                                    <PriceField
                                        activeChannelStockUnitDiscountIndex={
                                            activeChannelStockUnitDiscountIndices[0]
                                        }
                                        fieldWidth={fieldWidth}
                                        discountIndex={
                                            discountIndexByCSUId[csu.id?.toString() ?? '']
                                        }
                                        channelProductIndex={cpIndex}
                                        stockUnitIndex={suIndex}
                                    />
                                </td>
                            )
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
